import { Container } from "pixi.js";
import type { Log, PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { SpineArtboard, type SpineArtboardSpec, type SpineArtUrls } from "@slot/presentation/rive-spine";
import type { GameSceneHandle, LocalizationService, SceneDeps } from "@slot/game-shell";
import { loadDeferredVisualAssets, spineArtUrls } from "../assets/DeferredVisualAssets";
import { POTS } from "../rules/symbols";
import { loadGameVisualAssets, type GameVisualAssets } from "../assets/GameVisualAssets";
import { ART_QUALITY_TUNING, BOARD_IMPACT_TUNING, CELL_FIRE_TUNING, COIN_SHINE_TUNING, COLLECTOR_TUNING, EDGE_FIRE_TUNING, FIRE_STREAK_TUNING, FLYING_LIGHT_TUNING, IMPACT_TUNING, POT_ERUPTION_TUNING } from "../tuning/featureTuning";
import { BIG_WIN_MOTION, BONUS_TRANSITION_TUNING, PANEL_MOTION } from "../tuning/panelTuning";
import { BigWinView } from "../views/big-win/BigWinView";
import { FeatureFinishedView } from "../views/feature-finished/FeatureFinishedView";
import { FeatureTriggerView } from "../views/feature-trigger/FeatureTriggerView";
import { BonusTransition } from "../views/game-transition/BonusTransition";
import { GameTransitionView } from "../views/game-transition/GameTransitionView";
import { installBitmapFonts } from "../views/textStyles";
import { COMPOSITION_SIZE, GAME_LOGO, GameScene } from "./GameScene";
import { BOARD_IMPACT_SPINE, BONUS_TRANSITION_SPINE, CELL_FIRE_SPINE, CELL_HIT_SPINE, COIN_IDLE_SPINE, COLLECTOR_SPINE, EDGE_FIRE_BOTTOM_SPINE, EDGE_FIRE_TOP_SPINE, FIRE_STREAK_SPINE, FLYING_LIGHT_SPINE, IMPACT_SPINE, POT_ERUPTION_SPINE, POT_SHINE_SPINE, POT_SMOKE_SPINE, POTION_POTS_SPINE } from "./spine/convertedArt";

/**
 * The round's panels in paint order — the bonus's start, the big win, the bonus's end, the transition — behind the
 * shell's presenters. Every panel is UI chrome: its motion rides the chrome runner. The way into the bonus (the potion
 * explosion) is the transition's, played as the bonus's start is dismissed; that panel goes once it covers the screen.
 */
export class GameModals extends Container {
  public readonly featureTriggerView: FeatureTriggerView;
  public readonly bigWinView: BigWinView;
  public readonly featureFinishedView: FeatureFinishedView;
  public readonly gameTransitionView: GameTransitionView;
  /** The way into the bonus: the artist's potion explosion. */
  public readonly bonusTransition: BonusTransition;

  public constructor(input: { assets: GameVisualAssets; localization: LocalizationService; runner: PresentationTaskRunner; viewport: ViewportStore; log: Log }) {
    super({ label: "GameModalContainer" });
    const { assets, localization, runner, viewport, log } = input;
    this.bonusTransition = new BonusTransition({ viewport, runner, log });
    const triggerView = new FeatureTriggerView({ card: assets.textures.card, viewport, localization, runner, motion: PANEL_MOTION, entry: this.bonusTransition });
    this.featureTriggerView = triggerView;
    this.bigWinView = new BigWinView({ viewport, localization, runner, tuning: BIG_WIN_MOTION });
    this.featureFinishedView = new FeatureFinishedView({ card: assets.textures.card, viewport, localization, runner, motion: PANEL_MOTION });
    // The way in: the transition as the bonus's start is dismissed (the card still on screen); the way out has none.
    this.gameTransitionView = new GameTransitionView({ transition: this.bonusTransition, entering: () => triggerView.shown });
    this.addChild(this.featureTriggerView, this.bigWinView, this.featureFinishedView, this.gameTransitionView);
  }
}

/** This game's scene as the shell sees it, plus what its round presentation reads. */
export interface CoinHoldScene extends GameSceneHandle {
  game: GameScene;
  gameModals: GameModals;
  assets: GameVisualAssets;
}

/** The scene and the round's panels from the loaded assets. */
export function createScene(deps: SceneDeps): CoinHoldScene {
  installBitmapFonts();
  const assets = loadGameVisualAssets({ payload: deps.payload, log: deps.log });
  const services = deps.services;
  const format = (minor: string): string => services.moneyContext.formatter.formatMinor(minor, { omitSymbol: true });
  const game = new GameScene({
    assets,
    viewport: deps.viewport,
    contentArea: deps.contentArea,
    localization: services.localization,
    runner: services.presentationTaskRunner,
    ambientRunner: services.chromeTaskRunner,
    format,
    log: deps.log,
  });
  const gameModals = new GameModals({ assets, localization: services.localization, runner: services.chromeTaskRunner, viewport: deps.viewport, log: deps.log });
  // The art drawn only later (LIFE's flames on the meter, the mystery jackpot's reel) loads now, off the start: the first
  // spin never waits for it.
  game.trackArt(
    loadDeferredVisualAssets({ assets: deps.payload.assets, log: deps.log }).then((deferred) => {
      if (!deferred || game.destroyed) return;
      game.meter.setFlameArt(deferred.meterFlames);
      if (deferred.mysteryReel) game.fx.mysteryReel.setArt(deferred.mysteryReel);
    }),
  );
  // So do the artist's animations (decision 66's Rive files as Spine skeletons): the delivery's urns and today's sprite
  // flights stand in until they do. Each skeleton is drawn into a texture of its own by the renderer, on engine time.
  const files = { assets: deps.payload.assets, log: deps.log };
  const spineDeps = { renderer: deps.renderer, log: deps.log };
  // The looping art (the pots' potion, the plumes) at the device's rate: a phone draws it less often.
  const { loopFrameSeconds } = deps.viewport.getSnapshot().shape === "desktop" ? ART_QUALITY_TUNING.desktop : ART_QUALITY_TUNING.phone;
  const potsUrls = spineArtUrls(files, "potionPots");
  if (potsUrls) {
    game.trackArt(
      SpineArtboard.loadAll(potsUrls, [{ ...POTION_POTS_SPINE, frameSeconds: loopFrameSeconds }], spineDeps).then(([art]) => {
        if (art) game.pots.setLiveArt(art);
      }),
    );
  }
  // The shine behind the pots, always playing.
  const shineUrls = spineArtUrls(files, "potShine");
  if (shineUrls) {
    game.trackArt(
      SpineArtboard.loadAll(shineUrls, [{ ...POT_SHINE_SPINE, frameSeconds: loopFrameSeconds }], spineDeps).then(([art]) => {
        if (art) game.pots.shine.setArt(art);
      }),
    );
  }
  // GROW's fire on the grid's frame: its top and bottom pieces, two skeletons over one atlas, loaded once.
  const fireTop = spineArtUrls(files, "edgeFire", "top");
  const fireBottom = spineArtUrls(files, "edgeFire", "bottom");
  if (fireTop && fireBottom) {
    const { frameSeconds } = EDGE_FIRE_TUNING;
    const specs = [{ ...EDGE_FIRE_TOP_SPINE, frameSeconds }, { ...EDGE_FIRE_BOTTOM_SPINE, frameSeconds, skeleton: fireBottom.skeleton }];
    game.trackArt(
      SpineArtboard.loadAll(fireTop, specs, spineDeps).then(([top, bottom]) => {
        if (top && bottom) game.edgeFire.setArt({ top, bottom });
        else for (const art of [top, bottom]) art?.dispose();
      }),
    );
  }
  // The flying light: one skeleton per pot's colour, the files loaded once.
  const lightUrls = spineArtUrls(files, "flyingLight");
  if (lightUrls) {
    const spec = { ...FLYING_LIGHT_SPINE, frameSeconds: FLYING_LIGHT_TUNING.frameSeconds };
    game.trackArt(
      SpineArtboard.loadAll(lightUrls, POTS.map(() => spec), spineDeps).then(([life, multi, grow]) => {
        if (life && multi && grow) game.fx.lights.setArt({ life, multi, grow });
        else for (const art of [life, multi, grow]) art?.dispose();
      }),
    );
  }
  // The impact as a light reaches its pot: one skeleton per pot, the files loaded once.
  const impactUrls = spineArtUrls(files, "impact");
  if (impactUrls) {
    const spec = { ...IMPACT_SPINE, frameSeconds: IMPACT_TUNING.frameSeconds };
    game.trackArt(
      SpineArtboard.loadAll(impactUrls, POTS.map(() => spec), spineDeps).then(([life, multi, grow]) => {
        if (life && multi && grow) game.fx.impacts.setArt({ life, multi, grow });
        else for (const art of [life, multi, grow]) art?.dispose();
      }),
    );
  }
  // A pot erupting: one skeleton per pot, the files loaded once.
  const eruptionUrls = spineArtUrls(files, "potEruption");
  if (eruptionUrls) {
    const spec = { ...POT_ERUPTION_SPINE, frameSeconds: POT_ERUPTION_TUNING.frameSeconds };
    game.trackArt(
      SpineArtboard.loadAll(eruptionUrls, POTS.map(() => spec), spineDeps).then(([life, multi, grow]) => {
        if (life && multi && grow) game.fx.eruptions.setArt({ life, multi, grow });
        else for (const art of [life, multi, grow]) art?.dispose();
      }),
    );
  }
  // The fire streaks an erupting pot sends to the board: each pot's few, the files loaded once.
  const streakUrls = spineArtUrls(files, "fireStreak");
  if (streakUrls) {
    const spec = { ...FIRE_STREAK_SPINE, frameSeconds: FIRE_STREAK_TUNING.frameSeconds };
    const { perPot } = FIRE_STREAK_TUNING;
    const counts = POTS.map((kind) => perPot[kind]);
    game.trackArt(
      SpineArtboard.loadAll(streakUrls, counts.flatMap((count) => Array.from({ length: count }, () => spec)), spineDeps).then((arts) => {
        const loaded = arts.filter((art) => art !== null);
        if (loaded.length < arts.length) {
          for (const art of loaded) art.dispose();
          return;
        }
        const [life, multi] = [perPot.life, perPot.life + perPot.multi];
        game.fx.streaks.setArt({ life: loaded.slice(0, life), multi: loaded.slice(life, multi), grow: loaded.slice(multi) });
      }),
    );
  }
  // The activated pots' smoke: one skeleton per pot, the files loaded once.
  const smokeUrls = spineArtUrls(files, "potSmoke");
  if (smokeUrls) {
    const spec = { ...POT_SMOKE_SPINE, frameSeconds: loopFrameSeconds };
    game.trackArt(
      SpineArtboard.loadAll(smokeUrls, POTS.map(() => spec), spineDeps).then(([life, multi, grow]) => {
        if (life && multi && grow) game.pots.smoke.setArt({ life, multi, grow });
        else for (const art of [life, multi, grow]) art?.dispose();
      }),
    );
  }
  // GROW's board impact, as its fire streak lands on the board's centre.
  const boardImpactUrls = spineArtUrls(files, "boardImpact");
  if (boardImpactUrls) {
    game.trackArt(
      SpineArtboard.loadAll(boardImpactUrls, [{ ...BOARD_IMPACT_SPINE, frameSeconds: BOARD_IMPACT_TUNING.frameSeconds }], spineDeps).then(([art]) => {
        if (art) game.fx.boardImpact.setArt(art);
      }),
    );
  }
  // MULTI's hits on its cells, and the cells burning: each file loaded once for its few artboards; a fire loops, so at
  // the device's rate.
  const loadEach = (urls: SpineArtUrls | null, spec: SpineArtboardSpec, count: number, take: (arts: SpineArtboard[]) => void): void => {
    if (!urls) return;
    game.trackArt(
      SpineArtboard.loadAll(urls, Array.from({ length: count }, () => spec), spineDeps).then((arts) => {
        const loaded = arts.filter((art): art is SpineArtboard => art !== null);
        if (loaded.length === arts.length) take(loaded);
        else for (const art of loaded) art.dispose();
      }),
    );
  };
  loadEach(spineArtUrls(files, "cellHit"), { ...CELL_HIT_SPINE, frameSeconds: CELL_FIRE_TUNING.frameSeconds }, CELL_FIRE_TUNING.hits, (arts) => game.fx.cellHits.setArt(arts));
  loadEach(spineArtUrls(files, "cellFire"), { ...CELL_FIRE_SPINE, frameSeconds: loopFrameSeconds }, CELL_FIRE_TUNING.fires, (arts) => game.cellFires.setArt(arts));
  // The collect coins' collectors: the swirl loops, so at the device's rate too.
  loadEach(spineArtUrls(files, "collector"), { ...COLLECTOR_SPINE, frameSeconds: loopFrameSeconds }, COLLECTOR_TUNING.collectors, (arts) => game.collectors.setArt(arts));
  // The plain coins' shine: one artboard per variant, every coin on the board showing one; it loops, at the device's rate.
  loadEach(spineArtUrls(files, "coinIdle"), { ...COIN_IDLE_SPINE, frameSeconds: loopFrameSeconds }, COIN_SHINE_TUNING.variants, (arts) => game.coinShines.setArt(arts));
  // The way into the bonus: the potion explosion over the screen as the HOLD & WIN card is dismissed.
  loadEach(spineArtUrls(files, "bonusTransition"), { ...BONUS_TRANSITION_SPINE, frameSeconds: BONUS_TRANSITION_TUNING.frameSeconds }, 1, ([art]) => {
    if (art) gameModals.bonusTransition.setArt(art);
  });
  return {
    node: game,
    modalsNode: gameModals,
    compositionRoot: game.compositionRoot,
    compositionSize: COMPOSITION_SIZE,
    logo: GAME_LOGO,
    game,
    gameModals,
    assets,
  };
}
